import React, { useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Page, NamedLink, TrustBanner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ProgrammeFidelitePage.module.css';

/* ── Icône chevron bas (identique à la FAQ des fiches produits) ── */
const ChevronDown = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── FAQ item – même logique que la ListingPage ── */
const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={css.faqItem}>
      <button
        className={css.faqQuestion}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={css.faqQuestionText}>{question}</span>
        <ChevronDown
          className={`${css.faqIcon}${open ? ` ${css.faqIconOpen}` : ''}`}
        />
      </button>
      <p className={open ? css.faqAnswer : css.faqAnswerHidden}>{answer}</p>
    </div>
  );
};

/* ── Step card ── */
const Step = ({ number, icon, title, description }) => (
  <div className={css.step}>
    <div className={css.stepIcon}>{icon}</div>
    <div className={css.stepNumber}>{number}</div>
    <h3 className={css.stepTitle}>{title}</h3>
    <p className={css.stepDescription}>{description}</p>
  </div>
);

/* ── Page ── */
const ProgrammeFidelitePage = () => {
  const intl = useIntl();

  const steps = [
    {
      number: '1',
      icon: '🛒',
      title: intl.formatMessage({ id: 'ProgrammeFidelitePage.step1Title' }),
      description: intl.formatMessage({ id: 'ProgrammeFidelitePage.step1Description' }),
    },
    {
      number: '2',
      icon: '⭐',
      title: intl.formatMessage({ id: 'ProgrammeFidelitePage.step2Title' }),
      description: intl.formatMessage({ id: 'ProgrammeFidelitePage.step2Description' }),
    },
    {
      number: '3',
      icon: '🎁',
      title: intl.formatMessage({ id: 'ProgrammeFidelitePage.step3Title' }),
      description: intl.formatMessage({ id: 'ProgrammeFidelitePage.step3Description' }),
    },
  ];

  const faqItems = [
    {
      question: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq1Question' }),
      answer: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq1Answer' }),
    },
    {
      question: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq2Question' }),
      answer: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq2Answer' }),
    },
    {
      question: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq3Question' }),
      answer: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq3Answer' }),
    },
    {
      question: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq4Question' }),
      answer: intl.formatMessage({ id: 'ProgrammeFidelitePage.faq4Answer' }),
    },
  ];

  return (
    <Page
      title={intl.formatMessage({ id: 'ProgrammeFidelitePage.title' })}
      description={intl.formatMessage({ id: 'ProgrammeFidelitePage.metaDescription' })}
      scrollingDisabled={false}
    >
      <TopbarContainer />

      <main className={css.root}>

        {/* ── Hero ── */}
        <section className={css.hero}>
          <div className={css.heroContent}>
            <span className={css.heroBadge}>
              <FormattedMessage id="ProgrammeFidelitePage.heroBadge" />
            </span>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="ProgrammeFidelitePage.heroTitle" />
            </h1>
            <p className={css.heroSubtitle}>
              <FormattedMessage id="ProgrammeFidelitePage.heroSubtitle" />
            </p>
            <div className={css.heroCtas}>
              <NamedLink name="SignupPage" className={css.ctaPrimary}>
                <FormattedMessage id="ProgrammeFidelitePage.ctaSignup" />
              </NamedLink>
              <NamedLink name="CagnotteFidelitePage" className={css.ctaSecondary}>
                <FormattedMessage id="ProgrammeFidelitePage.ctaMyWallet" />
              </NamedLink>
            </div>
          </div>
        </section>

        {/* ── Vision ── */}
        <section className={css.visionSection}>
          <div className={css.container}>
            <div className={css.visionCard}>
              <h2 className={css.visionTitle}>
                <FormattedMessage id="ProgrammeFidelitePage.visionTitle" />
              </h2>
              <p className={css.visionText}>
                <FormattedMessage id="ProgrammeFidelitePage.visionText" />
              </p>
            </div>
          </div>
        </section>

        {/* ── Règles clés ── */}
        <section className={css.rulesSection}>
          <div className={css.container}>
            <div className={css.rulesGrid}>
              <div className={css.ruleCard}>
                <div className={css.ruleIcon}>🪙</div>
                <div className={css.ruleValue}>1</div>
                <div className={css.ruleLabel}>
                  <FormattedMessage id="ProgrammeFidelitePage.rule1Label" />
                </div>
              </div>
              <div className={css.ruleCard}>
                <div className={css.ruleIcon}>⭐</div>
                <div className={css.ruleValue}>500</div>
                <div className={css.ruleLabel}>
                  <FormattedMessage id="ProgrammeFidelitePage.rule2Label" />
                </div>
              </div>
              <div className={css.ruleCard}>
                <div className={css.ruleIcon}>🎁</div>
                <div className={css.ruleValue}>10€</div>
                <div className={css.ruleLabel}>
                  <FormattedMessage id="ProgrammeFidelitePage.rule3Label" />
                </div>
              </div>
              <div className={css.ruleCard}>
                <div className={css.ruleIcon}>📅</div>
                <div className={css.ruleValue}>12</div>
                <div className={css.ruleLabel}>
                  <FormattedMessage id="ProgrammeFidelitePage.rule4Label" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Comment ça marche ── */}
        <section className={css.stepsSection}>
          <div className={css.container}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="ProgrammeFidelitePage.howItWorksTitle" />
            </h2>
            <div className={css.stepsGrid}>
              {steps.map(step => (
                <Step key={step.number} {...step} />
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className={css.faqSection}>
          <div className={css.container}>
            <div className={css.faqInner}>
              <h2 className={css.faqTitle}>
                <FormattedMessage id="ProgrammeFidelitePage.faqTitle" />
              </h2>
              <div className={css.faqList}>
                {faqItems.map((item, idx) => (
                  <FaqItem key={idx} {...item} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA final ── */}
        <section className={css.ctaSection}>
          <h2 className={css.ctaSectionTitle}>
            <FormattedMessage id="ProgrammeFidelitePage.ctaSectionTitle" />
          </h2>
          <NamedLink name="SignupPage" className={css.ctaFinalButton}>
            <FormattedMessage id="ProgrammeFidelitePage.ctaSignup" />
          </NamedLink>
        </section>

        {/* ── Trust banner ── */}
        <TrustBanner />

      </main>

      <FooterContainer />
    </Page>
  );
};

export default ProgrammeFidelitePage;
